import { useState } from 'react';
import { api } from '../../shared/api/client';
import { Button, Card, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

interface Word { text: string; translit: string; strong: string; morph: string; gloss: string }
interface OriginalChapter { language: 'hebreo' | 'griego'; direction: 'rtl' | 'ltr'; verses: { verse: number; words: Word[] }[]; attribution: string }
interface Lexeme { strong: string; lemma: string; translit: string | null; gloss: string; count: number }

/** En la lectura: el capítulo en hebreo o griego, palabra por palabra, con significado y gramática en español. */
export function OriginalText({ book, chapter, verseStart, verseEnd, onClose }: { book: string; chapter: number; verseStart: number | null; verseEnd: number | null; onClose: () => void }) {
  const data = useAsync((signal) => api.get<OriginalChapter>(`/bible/original/chapter?book=${book}&chapter=${chapter}`, signal), [book, chapter]);
  const [selected, setSelected] = useState<Word | null>(null);
  const close = <Button variant="ghost" onClick={onClose}>Cerrar</Button>;

  if (data.status === 'loading') return <Card title="Texto original" action={close}><Loading /></Card>;
  if (data.status === 'error') return <Card title="Texto original" action={close}><ErrorState error={data.error} /></Card>;
  const { language, direction, verses, attribution } = data.data;
  const shown = verseStart === null ? verses : verses.filter((v) => (v.verse === 0 && verseStart === 1) || (v.verse >= verseStart && v.verse <= (verseEnd ?? verseStart)));
  return (
    <Card title={language === 'hebreo' ? 'Texto original en hebreo' : 'Texto original en griego'} action={close}>
      <p className="small muted">Toca una palabra para ver su significado, su gramática y cuántas veces aparece en la Biblia.</p>
      {selected ? <WordDetail word={selected} onClose={() => setSelected(null)} /> : null}
      <div className="original">
        {shown.map((v) => (
          <div key={v.verse} className="original__verse" dir={direction}>
            <span className="original__num">{v.verse === 0 ? 'Título' : v.verse}</span>
            {v.words.map((w, i) => (
              <button
                key={i}
                type="button"
                className={`original__word${selected === w ? ' original__word--on' : ''}`}
                onClick={() => setSelected(w)}
              >
                <span className="original__text" lang={language === 'hebreo' ? 'he' : 'grc'}>{w.text}</span>
                <span className="original__translit" dir="ltr">{w.translit}</span>
                <span className="original__gloss" dir="ltr">{w.gloss || '—'}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
      <p className="small muted atlas__credit">{attribution}</p>
    </Card>
  );
}

function WordDetail({ word, onClose }: { word: Word; onClose: () => void }) {
  const lex = useAsync((signal) => api.get<Lexeme>(`/bible/original/word/${word.strong}`, signal), [word.strong]);
  return (
    <div className="original__detail" role="dialog" aria-label="Detalle de la palabra">
      <p className="original__detail-word">
        <strong>{word.text}</strong> <span className="muted">{word.translit}</span>
      </p>
      <dl className="original__facts">
        <dt>Significado aquí</dt>
        <dd>{word.gloss || '—'}</dd>
        <dt>Gramática</dt>
        <dd>{word.morph || '—'}</dd>
        {lex.status === 'ready' ? (
          <>
            <dt>Palabra base</dt>
            <dd>
              {lex.data.lemma}
              {lex.data.translit ? ` (${lex.data.translit})` : ''} — {lex.data.gloss || '—'}
            </dd>
            <dt>En la Biblia</dt>
            <dd>{lex.data.count === 1 ? 'Aparece 1 vez' : `Aparece ${lex.data.count.toLocaleString('es')} veces`}</dd>
          </>
        ) : null}
        <dt>Número Strong</dt>
        <dd>{word.strong.replace(/^([HG])0*(\d+)([A-Z]?)$/, '$1$2$3')}</dd>
      </dl>
      <Button variant="ghost" onClick={onClose}>Cerrar detalle</Button>
    </div>
  );
}
